import React, { useEffect, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import Layout from 'components/sales/layout/layout';
import localization from 'assets/localization/localization';
import { BaseUrl } from 'contexts/baseUrl/baseUrl';
import Notfound from 'pages/notfound';
import {
  DivLayoutV2Section,
  DivLayoutV2Timeline,
  DivLayoutV2TimelineItem,
  DivLayoutV2NestedSection,
  DivLayoutV2SubCard,
  DivLayoutV2Block,
} from 'components/UI';
import CapexInvoicingDetails, { getCapexInvoicingDetails } from './CapexInvoicingDetails';
import ContractExtras from './ContractExtras';
import './siteDetailView.css';

const display = (val) =>
  val != null && val !== '' ? String(val) : localization.na;

const formatScopeOfWork = (scopeOfWork) => {
  if (scopeOfWork == null || scopeOfWork === '') return localization.na;
  return Number(scopeOfWork) === 2 ? 'Park+' : 'Client';
};

const formatInstallmentStatus = (status) => {
  if (status == null || status === '') return localization.na;
  const value = Number(status);
  if (value === 2) return 'Invoice Created';
  if (value === 1) return 'Not Sent To Recon';
  return localization.na;
};

const getLead = (siteDetail) => siteDetail?.lead || {};

const getMasterLeadAddress = (commercial) => {
  const addresses = commercial?.lead_addresses;
  if (!Array.isArray(addresses) || !addresses.length) return null;
  return addresses.find((a) => a.address_type === 'MASTER') || addresses[0];
};

const getLocationFields = (lead, masterAddress) => {
  const addr = masterAddress || {};
  return [
    {
      heading: localization.city,
      data: display(addr.city_name || lead.city),
    },
    { heading: localization.zone, data: display(addr.zone || lead.zone) },
    { heading: localization.area, data: display(addr.area || lead.area) },
    { heading: localization.state, data: display(addr.state || lead.state) },
    {
      heading: 'Pincode',
      data: display(addr.pin_code ?? addr.pincode ?? lead.pincode),
    },
    { heading: 'Geo State', data: display(addr.geo_state || lead.geo_state) },
    {
      heading: 'Geo City',
      data: display(addr.geo_city || addr.city || lead.geo_city),
    },
    {
      heading: 'Geo Pincode',
      data: display(addr.geo_pin ?? addr.geo_pincode ?? lead.geo_pincode),
    },
  ];
};

const buildPageTitle = (siteDetail) => {
  if (!siteDetail?.site_no) return localization.siteDetail;
  const statusLabel =
    siteDetail.site_status_label ??
    siteDetail.ops_status ??
    siteDetail.site_status;
  return (
    <>
      {localization.siteDetail}
      <span className="site-detail-header-meta">
        {' '}
        {display(siteDetail.site_no)} ({display(statusLabel)})
      </span>
    </>
  );
};

const getCapexInstallments = (contract) => {
  const installments = contract?.capex?.capex_installments;
  if (!installments || Array.isArray(installments)) {
    return { milestone_data: [], retention: [] };
  }
  return {
    milestone_data: installments.milestone_data || [],
    retention: installments.retention || [],
  };
};

const formatChannelPartner = (contract) => {
  const id = contract?.channel_partner_id;
  if (id == null || id === '' || Number(id) === 0) return null;
  const name = contract.channel_partner_name || contract.channel_partner;
  return name ? `${name} (${id})` : String(id);
};

const getContractFields = (contract, isParent = false) => {
  const fields = [
    { heading: 'Commercial Id', data: display(contract.commercial_id) },
    { heading: 'Commercial Type', data: display(contract.commercial_type) },
    { heading: 'Super Parent', data: display(contract.super_parent) },
    { heading: 'Parent Contract', data: display(contract.parent_contract) },
    { heading: 'Recon Contract Id', data: display(contract.recon_contract_id) },
    { heading: 'Installation Amount', data: display(contract.installation_amount) },
    { heading: 'Subscription Amount', data: display(contract.subscription_amount) },
    { heading: 'Scope of Work', data: formatScopeOfWork(contract.scope_of_work) },
    { heading: 'Contract From', data: display(contract.contract_from_date) },
    { heading: 'Contract To', data: display(contract.contract_to_date) },
  ];

  if (isParent) {
    fields.push(
      {
        heading: localization.installationStartDate,
        data: display(contract.installation_start_date),
      },
      {
        heading: localization.installationDeadline,
        data: display(contract.installation_deadline),
      }
    );
    const channelPartner = formatChannelPartner(contract);
    if (channelPartner) {
      fields.push({
        heading: localization.channelPartner,
        data: channelPartner,
      });
    }
  }

  return fields;
};

const getMilestoneFields = (row) => [
  { heading: 'Stage', data: display(row.stage_name) },
  { heading: 'Amount', data: display(row.amt) },
  { heading: 'Description', data: display(row.desc) },
  { heading: '%', data: display(row.percentage) },
  { heading: 'Invoice Creation', data: display(row.inv_creation_value) },
  {
    heading: 'Installment Status',
    data: formatInstallmentStatus(row.status ?? row.installment_status),
  },
];

const getRetentionFields = (row) => [
  { heading: 'Stage', data: display(row.post_stage_value) },
  { heading: 'Amount', data: display(row.amt) },
  { heading: 'Months', data: display(row.months) },
  { heading: 'Description', data: display(row.desc) },
  { heading: '%', data: display(row.percentage) },
  {
    heading: 'Installment Status',
    data: formatInstallmentStatus(row.status ?? row.installment_status),
  },
];

const ContractTimelineItem = ({ contract, title, isLast, isParent }) => {
  if (!contract) return null;

  const { milestone_data: milestones, retention } = getCapexInstallments(contract);
  const capexInvoicing = getCapexInvoicingDetails(contract);

  return (
    <DivLayoutV2TimelineItem
      badge={title}
      headerRight={contract.commercial_type}
      fields={getContractFields(contract, isParent)}
      isLast={isLast}
    >
      {capexInvoicing && (
        <CapexInvoicingDetails contract={contract} retention={retention} />
      )}
      <ContractExtras contract={contract} />
      {milestones.length > 0 && (
        <DivLayoutV2NestedSection title="Capex Milestones">
          {milestones.map((row, index) => (
            <DivLayoutV2SubCard
              key={row.milestone_identification_id || index}
              badge={
                row.milestone_status || row.stage_name || `Milestone ${index + 1}`
              }
              fields={getMilestoneFields(row)}
            />
          ))}
        </DivLayoutV2NestedSection>
      )}
      {retention.length > 0 && (
        <DivLayoutV2NestedSection title="Retention">
          {retention.map((row, index) => (
            <DivLayoutV2SubCard
              key={row.milestone_identification_id || index}
              badge={
                row.milestone_status ||
                row.post_stage_value ||
                `Retention ${index + 1}`
              }
              fields={getRetentionFields(row)}
            />
          ))}
        </DivLayoutV2NestedSection>
      )}
    </DivLayoutV2TimelineItem>
  );
};

ContractTimelineItem.propTypes = {
  contract: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  isLast: PropTypes.bool,
  isParent: PropTypes.bool,
};

const ContractsTimeline = ({ commercial, childContracts }) => {
  const items = [];
  if (commercial) {
    items.push({
      contract: commercial,
      title: 'Parent Contract',
      key: commercial.commercial_id || 'parent',
      isParent: true,
    });
  }
  childContracts.forEach((contract, index) => {
    items.push({
      contract,
      title:
        childContracts.length > 1 ? `Child Contract ${index + 1}` : 'Child Contract',
      key: contract.commercial_id || `child-${index}`,
      isParent: false,
    });
  });

  if (!items.length) return null;

  return (
    <DivLayoutV2Timeline>
      {items.map((item, index) => (
        <ContractTimelineItem
          key={item.key}
          contract={item.contract}
          title={item.title}
          isLast={index === items.length - 1}
          isParent={item.isParent}
        />
      ))}
    </DivLayoutV2Timeline>
  );
};

ContractsTimeline.propTypes = {
  commercial: PropTypes.object,
  childContracts: PropTypes.arrayOf(PropTypes.object),
};

const SiteDetailView = ({ getSiteDetail, siteDetail, match }) => {
  const { baseUrl } = useContext(BaseUrl);
  const siteId = match?.params?.siteId;
  const [isLoading, setIsLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!siteId || !getSiteDetail) return;
    (async () => {
      setIsLoading(true);
      setNotFound(false);
      const res = await getSiteDetail(siteId);
      setNotFound(!res);
      setIsLoading(false);
    })();
  }, [getSiteDetail, siteId]);

  if (notFound && !isLoading) {
    return <Notfound />;
  }

  const commercial = siteDetail?.commercial;
  const childContracts = siteDetail?.child_contracts || [];
  const hasContracts = commercial || childContracts.length > 0;
  const lead = getLead(siteDetail);
  const masterLeadAddress = getMasterLeadAddress(commercial);

  return (
    <Layout
      title={buildPageTitle(siteDetail)}
      isGoback
      linkFlag
      linkUrl={`${baseUrl}/site-dashboard`}
      useNewHeaderLayout
      isLoading={isLoading}
    >
      {siteDetail && (
        <div className="div-layout-v2 site-detail-view-page">
          <DivLayoutV2Section
            title="Lead"
            fields={[
              { heading: localization.projectId, data: display(lead.infra_project_id) },
              {
                heading: localization.leadNumber,
                data: display(lead.lead_number || lead.lead_no),
              },
              { heading: localization.leadType, data: display(lead.lead_type) },
              {
                heading: localization.clientBuildingOrPartnerName,
                data: display(lead.client_name),
              },
              { heading: localization.building_name, data: display(lead.building_name) },
              { heading: 'Location Type', data: display(lead.location_type) },
            ]}
          />

          <DivLayoutV2Section
            title="Location"
            fields={getLocationFields(lead, masterLeadAddress)}
          />

          <DivLayoutV2Section
            title="Assignment"
            fields={[
              { heading: localization.assignedTo, data: display(lead.assigned_to) },
              {
                heading: 'Assigned To (Installation)',
                data: display(siteDetail.assigned_to_installation),
              },
              {
                heading: 'Assigned To (Brand Activation)',
                data: display(siteDetail.assigned_to_ba),
              },
            ]}
          />

          <DivLayoutV2Block title="Contracts" className="site-detail-contracts-section">
            {hasContracts ? (
              <ContractsTimeline commercial={commercial} childContracts={childContracts} />
            ) : (
              <p className="div-layout-v2-empty mb-0">{localization.na}</p>
            )}
          </DivLayoutV2Block>
        </div>
      )}
    </Layout>
  );
};

SiteDetailView.propTypes = {
  getSiteDetail: PropTypes.func.isRequired,
  siteDetail: PropTypes.object,
  match: PropTypes.object,
};

export default SiteDetailView;
